// The published set times a person's own picks were built on, and what changed
// under them when a new bill was installed.
//
// Only picked sets count, and only a move in time or stage or a removal. A new
// set somebody has not picked, or a changed billing note, is not a warning.

import { diffSets } from './ingest.ts';
import type { Artist, FestivalSet, Id } from './types.ts';

export interface LineupChange {
  /** The set as it now stands, or the removed one when it is gone. */
  setId: Id;
  kind: 'changed' | 'removed';
  artistName: string;
  before: { dayId: Id; stageId: Id; startTs: number; endTs: number };
  after?: { dayId: Id; stageId: Id; startTs: number; endTs: number };
}

const shape = (s: FestivalSet) => ({ dayId: s.dayId, stageId: s.stageId, startTs: s.startTs, endTs: s.endTs });

export function pickedLineupChanges(
  previous: FestivalSet[],
  next: FestivalSet[],
  artists: Artist[],
  pickedSetIds: Set<Id>,
): LineupChange[] {
  const out: LineupChange[] = [];
  for (const change of diffSets(previous, next, artists)) {
    if (!pickedSetIds.has(change.setId) || !change.before) continue;
    const before = change.before as FestivalSet;
    if (change.kind === 'removed') {
      out.push({ setId: before.id, kind: 'removed', artistName: change.artistName, before: shape(before) });
      continue;
    }
    if (change.kind !== 'changed' || !change.after) continue;
    if (!change.fields?.some((f) => f === 'startTs' || f === 'endTs' || f === 'stageId')) continue;
    const after = change.after as FestivalSet;
    out.push({
      setId: after.id,
      kind: 'changed',
      artistName: change.artistName,
      before: shape(before),
      after: shape(after),
    });
  }
  return out;
}
